/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

function formatMoney(cents, format = "") {
  if (typeof cents === "string") {
    cents = cents.replace(".", "");
  }
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/, formatString = format || window.themeVariables.settings.moneyFormat;
  function defaultTo(value2, defaultValue) {
    return value2 == null || value2 !== value2 ? defaultValue : value2;
  }
  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultTo(precision, 2);
    thousands = defaultTo(thousands, ",");
    decimal = defaultTo(decimal, ".");
    if (isNaN(number) || number == null) {
      return 0;
    }
    number = (number / 100).toFixed(precision);
    let parts = number.split("."), dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands), centsAmount = parts[1] ? decimal + parts[1] : "";
    return dollarsAmount + centsAmount;
  }
  let value = "";
  switch (formatString.match(placeholderRegex)[1]) {
    case "amount":
      value = formatWithDelimiters(cents, 2);
      break;
    case "amount_no_decimals":
      value = formatWithDelimiters(cents, 0);
      break;
    case "amount_with_space_separator":
      value = formatWithDelimiters(cents, 2, " ", ".");
      break;
    case "amount_with_comma_separator":
      value = formatWithDelimiters(cents, 2, ".", ",");
      break;
    case "amount_with_apostrophe_separator":
      value = formatWithDelimiters(cents, 2, "'", ".");
      break;
    case "amount_no_decimals_with_comma_separator":
      value = formatWithDelimiters(cents, 0, ".", ",");
      break;
    case "amount_no_decimals_with_space_separator":
      value = formatWithDelimiters(cents, 0, " ");
      break;
    case "amount_no_decimals_with_apostrophe_separator":
      value = formatWithDelimiters(cents, 0, "'");
      break;
  }
  if (formatString.indexOf("with_comma_separator") !== -1) {
    return formatString.replace(placeholderRegex, value);
  } else {
    return formatString.replace(placeholderRegex, value);
  }
}

document.addEventListener('variant:changed', function(event) {
  let variant = event.detail.variant;
  let price = variant.price
  let priceholder = document.getElementById('productprice');
  let currencyFormat = window.themeVariables.settings.currencyCodeEnabled ? window.themeVariables.settings.moneyWithCurrencyFormat : window.themeVariables.settings.moneyFormat;
  if (variant.compare_at_price) {
    priceholder.innerHTML = `<span class="price price--highlight price--large">${formatMoney(variant["price"], currencyFormat)}</span><span class="price price--compare">${formatMoney(variant["compare_at_price"], currencyFormat)}</span>`;  
  } else {
    priceholder.innerHTML = `<span class="price price--highlight price--large">${formatMoney(variant["price"], currencyFormat)}</span>`;  
  }
});


// If secondary hover image is a video, play on hover
const imageWrappers = document.querySelectorAll(".product-item__image-wrapper");

imageWrappers.forEach((wrapper) => {
  const video = wrapper.querySelector(".product-item__secondary-video video");

  wrapper.addEventListener("mouseover", () => {
    if (video) {
      video.play();
    }
  });
});


// Hide a second set of variant options if more than one exists
(function(){

    // Get all items with classes "block-swatch__item" and "color-swatch__item"
    const swatchItems = document.querySelectorAll(".block-swatch__item, .color-swatch__item");
  
    // Loop through all variant options
    swatchItems.forEach((item) => {
      // Add a click event to each item
      item.addEventListener("click", () => {
        // Get all divs that have variant options in them
        const optionSelectors = document.querySelectorAll(".product-form__option-selector");
  
        // Set all the divs with variant options to block, so all variant options are shown after the first selection
        optionSelectors.forEach((selector) => {
          selector.style.display = "block";
        });
      });
    });
  
})()

// Hide the product-sticky-form__form div on page load
document.addEventListener("DOMContentLoaded", function() {
    var productStickyForm = document.querySelector('.product-sticky-form');
    productStickyForm.style.display = "none";
});

// Show the product-sticky-form__form div when a swatch is clicked
var swatchItems = document.querySelectorAll('.block-swatch__item, .color-swatch__item');
    swatchItems.forEach(function(swatch) {
    swatch.addEventListener('click', function() {
        var productStickyForm = document.querySelector('.product-sticky-form');
        productStickyForm.style.display = "block";
    });
});


// Force variant selection
// Allow default selection for the color option, but any other options will require the customer
// to select them explicitely.
// This is done by examining each product option swatch, checking the option name, and if it's not 'color', unselecting.
// A change event handler on the swatch elements will re-enable the add to cart button if all have a selection.
function forceVariantSelection() {
    let optionSelectors = document.querySelectorAll(".product-form__option-selector");
    // track if we actually uncheck any of the option swatches
    let uncheckedOptions = false;
  
    // exit if there are no options for this product
    if(optionSelectors.length < 2) {
      return;
    }
    
    // Loop through all swatches and if it's not color, uncheck all elements
    optionSelectors.forEach(optionSelector => {
      let optionName = selectorOptionName(optionSelector);

      /*
      if (optionName === "color") {
        return;
      }
      */
  
      // uncheck all elements
      let swatchRadios = optionSelector.querySelectorAll("input[type='radio']");
      swatchRadios.forEach(swatchRadio => {
        swatchRadio.checked = false;
        uncheckedOptions = true;
      });
    });
  
    // if we did uncheck any options, then we can remove the value of the id element
    // this allows us to take advantage of existing code paths in theme.js
    // that will correctly update the add to cart button when a variant is selected
    // If we didn't do this, then the user selecting the same variant as the page default
    // wouldn't trigger variant:changed and the atc button would not be updated
    if(uncheckedOptions) {
      // // if this is breakign things look at pins and aces for an alternative approach where I use a one-time event listener on the swatch list
      // // and manually change the variant id, which causes the themehandler code to emit an event
      // line below was used prior to 9/9/22
      // document.querySelectorAll("input[name=id]").forEach(input => {input.value = null});
  
      console.log("adding one-time event listener to force variant selection");
      // this theme has color-swatch-list as well, but we ignore that because we are ignoring
      // color options for forcing selection
      swatch = document.querySelector(".block-swatch-list, .color-swatch-list");
      swatch.addEventListener('click', forceSwatchSelection, {once: true});
  
      
      // If the klaviyo BIS trigger is displayed, don't mess with the ATC button as the theme has already handled it
      if(document.querySelector("#bis") && (document.querySelector("#bis").style.display != "none")) {
        console.log("forcevariantselection not adjusting atc as it detects BIS");
      } else {
        // Disable the Add to Cart button
        let atcButton = document.querySelector("#AddToCart");
        atcButton.disabled = true

        /*
        let atcSticky = document.querySelector("#StickyAddToCart");
        atcSticky.disabled = true
        atcSticky.innerText = "Make a Selection";
        */
       
        let atcText = atcButton.querySelector(".loader-button__text")
        atcText.innerText = "Make a Selection";
        atcButton.style.display="block";
        
        // let stickyPrice = document.querySelector(".product-sticky-form__price");
        // stickyPrice.innerText = "";
        // let stickyVariant = document.querySelector(".product-sticky-form__color");
        // stickyVariant.innerText = "";

        let defaultLabel = document.querySelector(".product-form__option-value");
        defaultLabel.innerText = "Make a Selection";

        /*
        let selectWrappers = document.querySelectorAll(".select-wrapper");
        selectWrappers[0].querySelector(".select__selected-value").innerText = "Select a Model";
        selectWrappers[1].querySelector(".select__selected-value").innerText = "Select an Option";
        */
        
      }
    }
  }
  
  // grab the productVariant obj and use it's product property to get the list of variants
  // if there's more than one, quickly switch to the next variant which will force the theme to update
  // the swatch control and then the 'real' click handler will set it to the clicked element in the swatch.
  // This fixes the bug where on initial page load, if you clicked the first variant in the swatch, nothing would 
  // happen (because it was already the selected variant id even though the swatch html did not indicate that)
  function forceSwatchSelection(event) {
      console.log('triggering initial option swatch update');
      const productVariantElement = document.querySelector('product-variants');
    
      if(!productVariantElement) return false;
    
      if(productVariantElement.product.variants.length > 1) {
        productVariantElement.selectVariant(productVariantElement.product.variants[1].id);
      }
  }
  
  // return a clean option name for this selector by downcasing and removing any colons
  function selectorOptionName(optionSelector) {
    return optionSelector.querySelector(".product-form__option-name").innerText.toLowerCase().replace(":","");
  }

  if(window.location.href.indexOf("/products/") > 0) {
    console.log("forcing variant selection");
    forceVariantSelection();
  }


// AfterShip Personalization
const getCart = async () => {
  const requestUrl = "/cart.js";
  const res = await window.fetch(requestUrl, {
    method: "GET",
  });
  const data = await res.json();
  if (!data) {
    return;
  }
  return data
}

const updateCart = async () => {
  const cart = await getCart()
  if (!cart) {
    return;
  }
  document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", {
    bubbles: true,
    detail: {
      cart,
      openMiniCart: true
    }
  }))
}

document.addEventListener('apz:added', updateCart)


// Ethercycle - Add captions to flickity for product page
async function updateProductImageCaption() {
  var i = await document.querySelector("flickity-carousel").flickityInstance;
  var alt = i.selectedElement.children[0].children[0].alt
  if(alt === undefined){var alt = ''}
  var caption = document.querySelector('.flickity-caption');
  caption.innerText = alt ;
}

// show initial caption once slider is loaded
window.addEventListener("flickity:ready", updateProductImageCaption);

// update captions as slides are changed
window.addEventListener("flickity:slide-changed", updateProductImageCaption);